import { Injectable, inject } from '@angular/core';
import {
  AuthService as Auth0Service,
  RedirectLoginOptions,
} from '@auth0/auth0-angular';
import { Observable, map, catchError, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoggingService } from '@fleet-monitor/frontend-logging';
import { forgetRemindLater } from '../state/terms-reminder';

/**
 * Namespace for custom ID-token claims. Auth0 silently drops non-namespaced
 * custom claims, so the post-login Action must stamp `client`/`grants`/
 * `permissions` under this namespace (e.g.
 * `https://api.fleet.meterfeeder.com/permissions`) and we read the same keys
 * here. Keep this in sync with the "Assign Client Claim" Action in
 * auth0/actions/post-login — see docs/CLIENT_API_TOKENS.md.
 */
export const AUTH0_CLAIM_NAMESPACE = 'https://api.fleet.meterfeeder.com/';

/**
 * Value of the namespaced `permissions` claim that marks a global administrator.
 * Mirrors `app_metadata.permissions === 'ADMINISTRATOR'` as stamped by the
 * post-login Action, and the API's server-side `checkPermission(Administrator)`.
 */
export const GLOBAL_ADMIN_PERMISSION = 'ADMINISTRATOR';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth0 = inject(Auth0Service);
  private logger = inject(LoggingService);

  login(): void {
    this.auth0.loginWithRedirect({
      authorizationParams: {
        redirect_uri: window.location.origin + '/callback',
      },
      appState: { target: '/dashboard' },
    });
  }

  // Direct users to Auth0's signup screen
  signUp(appState: RedirectLoginOptions<any>['appState'] = {}): void {
    this.auth0.loginWithRedirect({
      authorizationParams: {
        redirect_uri: window.location.origin + '/callback',
        screen_hint: 'signup',
      },
      appState,
    });
  }

  logout(): void {
    forgetRemindLater();
    this.auth0.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }

  get isAuthenticated$(): Observable<boolean> {
    return this.auth0.isAuthenticated$;
  }

  get isLoading$() {
    return this.auth0.isLoading$;
  }

  get user$() {
    return this.auth0.user$;
  }

  get token$(): Observable<string | undefined> {
    // For E2E testing, return a fake token
    if (
      !environment.production &&
      window.localStorage.getItem('E2E_TEST_AUTH') === '1'
    ) {
      return of('fake-e2e-test-token');
    }
    return this.auth0
      .getAccessTokenSilently({
        authorizationParams: {
          audience: environment.auth0.audience,
          scope: 'openid profile email offline_access',
        },
      })
      .pipe(
        tap((token) => {
          if (!token) {
            this.logger.warn(
              '[AuthService] Access token is empty for current session'
            );
          }
        }),
        catchError((error) => {
          this.logger.error('[AuthService] getAccessTokenSilently failed', error);
          // If silent refresh fails, redirect to login
          if (
            error.error === 'login_required' ||
            error.error_description?.includes('Missing Refresh Token')
          ) {
            this.logger.warn(
              '[AuthService] Refresh token missing or expired, redirecting to login'
            );
            this.login();
          }
          return of(undefined);
        })
      );
  }

  hasPermission(permission: string): Observable<boolean> {
    return this.user$.pipe(
      map((user) => user?.['permissions']?.includes(permission) || false)
    );
  }

  // E2E override: in a non-production build with the E2E auth flag set, the
  // Auth0 SDK is not really logged in, so user$ carries no claims. Read the
  // client-user claims from localStorage instead (mirrors the token$ bypass).
  private e2eEnabled(): boolean {
    return (
      !environment.production &&
      typeof window !== 'undefined' &&
      window.localStorage.getItem('E2E_TEST_AUTH') === '1'
    );
  }

  /**
   * The client id a human client-user is linked to (from the namespaced
   * `client` claim), or undefined for users with no client association.
   */
  get clientId$(): Observable<string | undefined> {
    if (this.e2eEnabled()) {
      return of(window.localStorage.getItem('E2E_TEST_CLIENT') || undefined);
    }
    return this.user$.pipe(
      map(
        (user) =>
          user?.[`${AUTH0_CLAIM_NAMESPACE}client`] as string | undefined
      )
    );
  }

  /**
   * Whether the user is a global administrator, read from the namespaced
   * `permissions` claim (`ADMINISTRATOR`). This is defense-in-UX only — the API
   * independently enforces admin access with `checkPermission(Administrator)`.
   */
  get isGlobalAdmin$(): Observable<boolean> {
    if (this.e2eEnabled()) {
      return of(window.localStorage.getItem('E2E_TEST_ADMIN') === '1');
    }
    return this.user$.pipe(
      map(
        (user) =>
          user?.[`${AUTH0_CLAIM_NAMESPACE}permissions`] ===
          GLOBAL_ADMIN_PERMISSION
      )
    );
  }

  /**
   * Whether the user carries a client-scoped RBAC grant (from the namespaced
   * `grants` claim), e.g. ClientGrant.UpdateBillingPayment.
   */
  hasGrant(grant: string): Observable<boolean> {
    if (this.e2eEnabled()) {
      const grants = (
        window.localStorage.getItem('E2E_TEST_GRANTS') || ''
      ).split(',');
      return of(grants.includes(grant));
    }
    return this.user$.pipe(
      map(
        (user) =>
          (user?.[`${AUTH0_CLAIM_NAMESPACE}grants`] as string[] | undefined)?.includes(
            grant
          ) || false
      )
    );
  }

  /**
   * Validates that the user has a valid token before proceeding.
   * If token validation fails, redirects to login.
   * @returns Observable<boolean> - true if token is valid, false otherwise
   */
  validateToken(): Observable<boolean> {
    return this.token$.pipe(
      map((token) => !!token),
      catchError((error) => {
        this.logger.error('[AuthService] Token validation failed', error);
        this.login();
        return of(false);
      })
    );
  }
}
